/**
=========================================================
* Soft UI Dashboard PRO React - v3.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import gradientBg from '../../images/BlurBackground.jpg';
import bgBlue from '../../images/background-blue.svg';
import bgPurple from '../../images/background-purple.svg';

import deepmerge from 'deepmerge';
import {
    isShyroHealthCustomTheme,
    isCheckpointCustomTheme,
    isWhiteLabelExampleTheme,
} from './is-whitelabel';
import { colorValues } from '../design-system/tokens';

/**
 * The base colors for the Soft UI Dashboard PRO Material.
 * You can add new color using this file.
 * You can customized the colors for the entire Soft UI Dashboard PRO Material using thie file.
 */

const colors = {
    background: {
        default: colorValues.neutral['0'],
    },

    transparent: {
        main: 'rgba(255,255,255,0)',
    },

    white: {
        main: colorValues.neutral['0'],
        focus: colorValues.neutral['0'],
    },

    black: {
        light: colorValues.neutral['900'],
        main: colorValues.neutral['950'],
        focus: colorValues.neutral['950'],
    },

    primary: {
        main: colorValues.accent1['600'],
        focus: colorValues.accent1['600'],
    },

    secondary: {
        main: colorValues.neutral['500'],
        focus: colorValues.neutral['400'],
    },

    action: {
        main: colorValues.accent2['500'],
        focus: colorValues.accent2['500'],
    },

    info: {
        main: colorValues.accent2['400'],
        focus: colorValues.accent2['400'],
    },

    success: {
        main: colorValues.success['500'],
        focus: colorValues.success['600'],
    },

    warning: {
        main: colorValues.attention['400'],
        focus: colorValues.attention['500'],
    },

    error: {
        main: colorValues.critical['600'],
        focus: colorValues.critical['700'],
    },

    offWhite: {
        main: colorValues.neutral['100'],
        focus: colorValues.neutral['100'],
    },

    // old "light" defined in SoftUI
    light: {
        main: colorValues.neutral['200'],
        focus: colorValues.neutral['200'],
    },

    // currently defined as "light" in Figma
    lightNew: {
        main: colorValues.neutral['200'],
        focus: colorValues.neutral['200'],
    },

    dark: {
        main: colorValues.neutral['700'],
        focus: colorValues.neutral['700'],
    },

    body: {
        main: colorValues.neutral['800'],
        focus: colorValues.neutral['800'],
    },

    grey: {
        100: colorValues.neutral['100'],
        200: colorValues.neutral['200'],
        300: colorValues.neutral['300'],
        400: colorValues.neutral['400'],
        500: colorValues.neutral['500'],
        600: colorValues.neutral['600'],
        700: colorValues.neutral['700'],
        800: colorValues.neutral['800'],
        900: colorValues.neutral['900'],
    },

    gradients: {
        primary: {
            main: colorValues.accent1['800'],
            state: colorValues.accent1['600'],
        },

        secondary: {
            main: colorValues.neutral['500'],
            state: colorValues.neutral['400'],
        },

        info: {
            main: colorValues.accent2['700'],
            state: colorValues.accent2['400'],
        },

        success: {
            main: colorValues.success['700'],
            state: colorValues.success['400'],
        },

        warning: {
            main: colorValues.attention['700'],
            state: colorValues.attention['400'],
        },

        error: {
            main: colorValues.critical['800'],
            state: colorValues.critical['400'],
        },

        light: {
            main: colorValues.neutral['300'],
            state: colorValues.neutral['100'],
        },

        dark: {
            main: colorValues.neutral['800'],
            state: colorValues.neutral['600'],
        },
    },

    socialMediaColors: {
        facebook: {
            main: '#3b5998',
            dark: '#344e86',
        },

        twitter: {
            main: '#55acee',
            dark: '#3ea1ec',
        },

        instagram: {
            main: '#125688',
            dark: '#0e456d',
        },

        linkedin: {
            main: '#0077b5',
            dark: '#00669c',
        },

        pinterest: {
            main: '#cc2127',
            dark: '#b21d22',
        },

        youtube: {
            main: '#e52d27',
            dark: '#d41f1a',
        },

        vimeo: {
            main: '#1ab7ea',
            dark: '#13a3d2',
        },

        slack: {
            main: '#3aaf85',
            dark: '#329874',
        },

        dribbble: {
            main: '#ea4c89',
            dark: '#e73177',
        },

        github: {
            main: '#24292e',
            dark: '#171a1d',
        },

        reddit: {
            main: '#ff4500',
            dark: '#e03d00',
        },

        tumblr: {
            main: '#35465c',
            dark: '#2a3749',
        },
    },

    alertColors: {
        primary: {
            main: colorValues.critical['800'],
            state: colorValues.critical['600'],
            border: colorValues.critical['400'],
        },

        secondary: {
            main: colorValues.critical['600'],
            state: colorValues.critical['400'],
            border: colorValues.critical['200'],
        },

        info: {
            main: colorValues.accent2['600'],
            state: colorValues.accent2['400'],
            border: colorValues.accent2['200'],
        },

        success: {
            main: colorValues.success['600'],
            state: colorValues.success['400'],
            border: colorValues.success['200'],
        },

        warning: {
            main: colorValues.attention['600'],
            state: colorValues.attention['400'],
            border: colorValues.attention['200'],
        },

        error: {
            main: colorValues.critical['600'],
            state: colorValues.critical['400'],
            border: colorValues.critical['200'],
        },

        light: {
            main: colorValues.neutral['300'],
            state: colorValues.neutral['200'],
            border: colorValues.neutral['50'],
        },

        dark: {
            main: colorValues.neutral['900'],
            state: colorValues.neutral['700'],
            border: colorValues.neutral['300'],
        },
    },

    badgeColors: {
        primary: {
            background: colorValues.accent1['200'],
            text: colorValues.accent1['600'],
        },

        secondary: {
            background: colorValues.neutral['300'],
            text: colorValues.neutral['600'],
        },

        info: {
            background: colorValues.accent2['200'],
            text: colorValues.accent2['600'],
        },

        success: {
            background: colorValues.success['200'],
            text: colorValues.success['600'],
        },

        warning: {
            background: colorValues.attention['200'],
            text: colorValues.attention['600'],
        },

        error: {
            background: colorValues.critical['200'],
            text: colorValues.critical['600'],
        },

        light: {
            background: colorValues.neutral['50'],
            text: colorValues.neutral['400'],
        },

        dark: {
            background: colorValues.neutral['300'],
            text: colorValues.neutral['800'],
        },
    },

    inputColors: {
        borderColor: {
            main: colorValues.neutral['300'],
            focus: colorValues.accent2['400'],
        },
        boxShadow: colorValues.accent2['200'],
        error: colorValues.critical['600'],
        success: colorValues.success['600'],
    },

    sliderColors: {
        thumb: { borderColor: colorValues.neutral['300'] },
    },

    circleSliderColors: {
        background: colorValues.neutral['300'],
    },

    tabs: {
        indicator: { boxShadow: colorValues.neutral['300'] },
    },

    gradientBackground: {
        image: gradientBg,
        fallbackColor: colorValues.accent2['950'], // body
        textColor: colorValues.neutral['400'],
        textColorActive: colorValues.neutral['50'],
    },

    permissionCheck: {
        background: {
            default:
                'linear-gradient(180deg, rgba(52, 71, 103, 0.7) 0%, #344767 100%)',
            error: 'linear-gradient(180deg, rgba(234, 6, 6, 0.56) 0%, rgba(234, 6, 6, 0.8) 100%)',
        },
    },

    hairCheck: {
        deviceToggle: {
            iconColor: 'dark' as const,
            textColor: 'dark' as const,
        },
    },

    videoCall: {
        tray: {
            button: {
                normal: 'secondary' as const,
                highlighted: 'primary' as const,
            },
        },
    },

    audioRecorder: {
        background: {
            virtual: bgBlue,
            'in-person': bgPurple,
        },
    },

    gradientButton: {
        primary: {
            background:
                'linear-gradient(106deg, #7086FB 7.01%, #5E1ACC 85.58%)',
        },
        white: {
            background: 'linear-gradient(180deg, #FFF 0%, #D4D0E4 100%)',
        },
        danger: {
            background:
                'linear-gradient(155deg, #FFA28A 1.45%, #E95833 58.06%)',
        },
    },
};

const shyrohealthColors = deepmerge(colors, {
    primary: {
        main: '#76c9ba',
        focus: '#76c9ba',
    },
    hairCheck: {
        deviceToggle: {
            iconColor: 'primary' as const,
            textColor: 'primary' as const,
        },
    },
    videoCall: {
        tray: {
            button: {
                highlighted: 'secondary' as const,
            },
        },
    },
    gradientBackground: {
        image: null,
        fallbackColor: '#f4f4ef',
        textColor: 'text',
        textColorActive: 'text',
    },
    permissionCheck: {
        background: {
            default: '#76c9ba',
        },
    },
});

// Also for design system libs/design-system/src/lib/theme/design-system/color-modes/light.ts
const checkpointColors = deepmerge(colors, {
    primary: {
        main: '#028ba6',
        focus: '#028ba6',
    },
    dark: {
        main: '#252525',
        focus: '#252525',
    },
    secondary: {
        main: colors.secondary.main,
        focus: colors.secondary.main,
    },
    lightNew: {
        main: colors.white.main,
        focus: colors.white.main,
    },
    action: {
        main: '#26c6da',
        focus: '#26c6da',
    },
    gradientBackground: {
        image: null,
        fallbackColor: '#028ba6',
        textColor: 'text',
        textColorActive: 'text',
    },
    hairCheck: {
        deviceToggle: {
            iconColor: '#028ba6',
            textColor: '#028ba6',
        },
    },
    // Audio recorder specific overrides can be added here
    audioRecorder: {
        background: {
            virtual: undefined, // Can be customized later
            'in-person': undefined, // Can be customized later
        },
    },
    // Custom button specific overrides can be added here
    gradientButton: {
        primary: {
            background:
                'linear-gradient(106deg, #028ba6 7.01%, #028ba6 85.58%)',
        },
        white: {
            background:
                'linear-gradient(106deg, #ffffff 7.01%, #ffffff 85.58%)',
        },
        danger: {
            background:
                'linear-gradient(106deg, #252525 7.01%, #252525 85.58%)',
        },
    },
});

// Also for design system libs/design-system/src/lib/theme/design-system/color-modes/light.ts
const exampleWhiteLabelColors = deepmerge(colors, {
    primary: {
        main: '#0f0',
        focus: '#0f0',
    },
    body: {
        main: '#f00',
        focus: '#f00',
    },
    dark: {
        main: '#00f',
        focus: '#00f',
    },
    offWhite: {
        main: '#0f0',
        focus: '#0f0',
    },
    gradientBackground: {
        image: null,
        fallbackColor: '#000',
        textColor: 'text',
        textColorActive: 'text',
    },
    // Audio recorder specific overrides can be added here
    audioRecorder: {
        background: {
            virtual: undefined, // Can be customized later
            'in-person': undefined, // Can be customized later
        },
    },
    // Custom button specific overrides can be added here
    gradientButton: {
        primary: {
            background:
                'linear-gradient(106deg, #ff0000 7.01%, #ffffff 85.58%)',
        },
        white: {
            background:
                'linear-gradient(106deg, #00ff00 7.01%, #ffffff 85.58%)',
        },
        danger: {
            background:
                'linear-gradient(106deg, #0000ff 7.01%, #ffffff 85.58%)',
        },
    },
});

if (isShyroHealthCustomTheme) {
    Object.assign(colors, shyrohealthColors);
}

if (isCheckpointCustomTheme) {
    Object.assign(colors, checkpointColors);
}

if (isWhiteLabelExampleTheme) {
    Object.assign(colors, exampleWhiteLabelColors);
}

export default colors;
